<script lang="ts">
    import { createEventDispatcher } from 'svelte';
    import { _ } from 'svelte-i18n';
    import { get } from 'svelte/store';
    import { openModal } from 'svelte-modals';
    import { api } from '../../../stores/api';
    import ButtonMain from '../../shared/ButtonMain.svelte';
    import { Navigation } from '../../utilities/Navigation';
    import { TacticalPlanApi } from './TacticalPlanApi';
    import ModalTacticalPlanJsonImportResult from './ModalTacticalPlanJsonImportResult.svelte';

    const dispatch = createEventDispatcher<{ imported: { tacticalPlanId: number } }>();

    let fileInput: HTMLInputElement;
    let importing: boolean = false;

    function showResult(statusMessage: string, errors: string[], warnings: string[] = [], planId: number | null = null) {
        openModal(ModalTacticalPlanJsonImportResult, {
            statusMessage,
            errors,
            warnings,
            planId,
            onOpenPlan: (id: number) => Navigation.navigate('/tactical-plan/' + id),
        });
    }

    async function handleFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;

        importing = true;
        try {
            let planData: unknown;
            try {
                planData = JSON.parse(await file.text());
            } catch (err) {
                showResult($_('tp_import_json_rejected', { values: { name: file.name } }), [
                    $_('tp_import_json_invalid_file', { values: { name: file.name, error: String(err) } }),
                ]);
                return;
            }

            const result = await TacticalPlanApi.importFromJson(get(api), planData);
            if (result.imported && result.tactical_plan_id !== null) {
                const mapsCreated =
                    result.maps_count > 0
                        ? ' ' +
                          $_('tp_import_json_maps_created', {
                              values: { maps: result.maps_count, zones: result.zones_count },
                          })
                        : '';
                showResult(
                    $_('tp_import_json_success', {
                        values: { name: result.name, count: result.rules_count },
                    }) + mapsCreated,
                    [],
                    result.warnings,
                    result.tactical_plan_id
                );
                dispatch('imported', { tacticalPlanId: result.tactical_plan_id });
            } else {
                showResult($_('tp_import_json_rejected', { values: { name: file.name } }), result.errors);
            }
        } catch (err) {
            console.error('TacticalPlanJsonImportButton', err);
            showResult($_('tp_import_json_rejected', { values: { name: file.name } }), [
                $_('tp_import_json_request_failed', { values: { error: $_(String(err)) } }),
            ]);
        } finally {
            importing = false;
            input.value = '';
        }
    }
</script>

<input
    bind:this={fileInput}
    type="file"
    accept=".json,application/json"
    style="display: none;"
    data-testid="tactical-plan-json-import-input"
    on:change={handleFileSelected}
/>
<ButtonMain
    disabled={importing}
    text={$_('tp_import_json_button')}
    on:click={() => fileInput.click()}
/>
